import React from 'react';

export type AlertType = 'info' | 'success' | 'warning' | 'danger' | 'dark';

interface AlertProps {
  type: AlertType;
  title?: string;
  children: React.ReactNode;
  className?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
}

const alertStyles = {
  info: {
    container: 'bg-primary/10 border border-primary/20 text-primary',
    title: 'text-primary',
    text: 'text-primary/80',
    icon: 'text-primary/60'
  },
  success: {
    container: 'bg-green-50 border border-green-200 text-green-800',
    title: 'text-green-800',
    text: 'text-green-700',
    icon: 'text-green-400'
  },
  warning: {
    container: 'bg-yellow-50 border border-yellow-200 text-yellow-800',
    title: 'text-yellow-800',
    text: 'text-yellow-700',
    icon: 'text-yellow-400'
  },
  danger: {
    container: 'bg-red-50 border border-red-200 text-red-800',
    title: 'text-red-800',
    text: 'text-red-700',
    icon: 'text-red-400'
  },
  dark: {
    container: 'bg-gray-50 border border-gray-200 text-gray-800',
    title: 'text-gray-800',
    text: 'text-gray-700',
    icon: 'text-gray-400'
  }
};

const InfoIcon = () => (
  <svg className="h-5 w-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
  </svg>
);

const SuccessIcon = () => (
  <svg className="h-5 w-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
  </svg>
);

const WarningIcon = () => (
  <svg className="h-5 w-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM10 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm0-4a1 1 0 0 1-1-1V6a1 1 0 0 1 2 0v4a1 1 0 0 1-1 1Z"/>
  </svg>
);

const DangerIcon = () => (
  <svg className="h-5 w-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z"/>
  </svg>
);

const DarkIcon = () => (
  <svg className="h-5 w-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
  </svg>
);

const CloseIcon = () => (
  <svg className="h-4 w-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
  </svg>
);

const getIcon = (type: AlertType) => {
  switch (type) {
    case 'info':
    case 'dark':
      return <InfoIcon />;
    case 'success':
      return <SuccessIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'danger':
      return <DangerIcon />;
    default:
      return <InfoIcon />;
  }
};

export default function Alert({ 
  type, 
  title, 
  children, 
  className = '', 
  dismissible = false, 
  onDismiss 
}: AlertProps) {
  const styles = alertStyles[type];

  return (
    <div 
      className={`rounded-lg p-4 ${styles.container} ${className}`}
      role="alert"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <div className={styles.icon}>
            {getIcon(type)}
          </div>
        </div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={`text-sm font-medium ${styles.title}`}>
              {title}
            </h3>
          )}
          <div className={`${title ? 'mt-2' : ''} text-sm ${styles.text}`}>
            {children}
          </div>
        </div>
        {dismissible && onDismiss && (
          <div className="ml-auto pl-3">
            <button
              type="button"
              className={`inline-flex rounded-md p-1.5 ${styles.icon} hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent`}
              onClick={onDismiss}
              aria-label="Dismiss"
            >
              <CloseIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Convenience components for common use cases
export const InfoAlert = (props: Omit<AlertProps, 'type'>) => <Alert type="info" {...props} />;
export const SuccessAlert = (props: Omit<AlertProps, 'type'>) => <Alert type="success" {...props} />;
export const WarningAlert = (props: Omit<AlertProps, 'type'>) => <Alert type="warning" {...props} />;
export const DangerAlert = (props: Omit<AlertProps, 'type'>) => <Alert type="danger" {...props} />;
export const DarkAlert = (props: Omit<AlertProps, 'type'>) => <Alert type="dark" {...props} />;
